/* eslint-disable complexity */
import { Delete as DeleteIcon, PlayArrow, RotateRight, Texture } from '@mui/icons-material';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, Slider, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { EAttributeType, EAttributeUnit } from '../../../@types/AttributeTypes';
import { ETextureOrigin, IProductTexture, ITexture } from '../../../@types/ContextProviderRelated';
import { EActiveDrawer, EElementType, EUnitType } from '../../../@types/Enums';
import { IFilesApiObject } from '../../../@types/FilesAPIModels';
import { IconReset, IconTriangleSideA, IconTriangleSideB, IconTriangleSideC } from '../../../assets/icons';

import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import ElementModel, { IElement } from '../../../models/standard/ElementModel';
import { IObject, ObjectModel } from '../../../models/standard/ObjectModel';

import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useSelection } from '../../../provider/SelectionProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { AttributeSelectionInput, mergeAttributesById, resolveAttributeSelection } from '../../../utils/attributes/attributeCreationUtils';
import { attributeUnitDisplayMap, convertAttributeValue, getCorrespondingUnit } from '../../../utils/attributes/attributeUtils';

import { DEFAULT_OBJECT_IDS } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { immersightBlue, immersightBlue75, immersightGreyLighter3 } from '../../../utils/colors';
import { MM_TO_INTERNAL_FACTOR } from '../../../utils/constants';
import { inchToInternalRounded, internalToInchesRounded } from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';

import ConfirmationDialog from '../../modals/ConfirmationDialog';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';
import AttributeListFixed from '../AttributeListFixed';
import ObjectAutocomplete from '../ObjectAutocomplete';

const MIN_MM = 10;
const MAX_MM = 10000;

interface ElementDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
  selectedElement: { selectedElementIndex: number };
  resetSelection: () => void;
}

const ElementDrawer: React.FC<ElementDrawerProps> = ({ id, open, onClose, selectedElement, resetSelection }) => {
  const intl = useIntl();
  const { building, allElements, updateElement, deleteElement, setSelectedModel: setSelectedObject } = useBuilding();
  const { unitType } = useUnitContext();
  const { setActiveDrawer } = useSelection();

  const element: IElement | undefined = allElements[selectedElement.selectedElementIndex];

  const initialRotation = element ? Math.round((element.rotation * 180) / Math.PI) : 0;
  
  const initialLength = element
    ? unitType === EUnitType.METRIC
      ? Math.round(element.length / MM_TO_INTERNAL_FACTOR)
      : Math.max(1, Math.round(internalToInchesRounded(element.length)))
    : 0;
  const initialWidth = element
    ? unitType === EUnitType.METRIC
      ? Math.round(element.width / MM_TO_INTERNAL_FACTOR)
      : Math.max(1, Math.round(internalToInchesRounded(element.width)))
    : 0;
  const initialSideC = element
    ? unitType === EUnitType.METRIC
      ? Math.round((element.sideC || 0) / MM_TO_INTERNAL_FACTOR)
      : Math.max(1, Math.round(internalToInchesRounded(element.sideC || 0)))
    : 0;
  const initialTextureRotation = element ? Math.round(((element.texture?.rotation ?? 0) * 180) / Math.PI) : 0;

  const [length, setLength] = useState<number>(initialLength);
  const [width, setWidth] = useState<number>(initialWidth);
  const [sideC, setSideC] = useState<number>(initialSideC);
  const [rotation, setRotation] = useState<number>(initialRotation);
  const [shifting, setShifting] = useState<number>(element?.texture?.offset ?? 0);
  const [textureRotation, setTextureRotation] = useState<number>(initialTextureRotation);

  const [lengthError, setLengthError] = useState<string>('');
  const [widthError, setWidthError] = useState<string>('');
  const [sideCError, setSideCError] = useState<string>('');

  const [objects, setObjects] = useState<IFilesApiObject[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedObjectLocal, setSelectedObjectLocal] = useState<IFilesApiObject | null>(element?.object || null);
  const [inputValue, setInputValue] = useState<string>(element?.object?.name || '');
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>(
    element?.object?.attributes.reduce(
      (acc, a) => {
        if (a.value) acc[a.id] = `${a.value}`;
        return acc;
      },
      {} as Record<number, string>,
    ) || {},
  );
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>(
    element?.object?.attributes.reduce(
      (acc, a) => {
        acc[a.id] = !!a.value;
        return acc;
      },
      {} as Record<number, boolean>,
    ) || {},
  );

  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState<string>('');
  const [attrError, setAttrError] = useState<string | null>(null);
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const mmLabel = useMemo(() => intl.formatMessage({ id: 'unit.millimeter', defaultMessage: 'mm' }), [intl]);
  const inLabel = useMemo(() => intl.formatMessage({ id: 'unit.inch', defaultMessage: 'in' }), [intl]);
  const yesLabel = useMemo(() => intl.formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' }), [intl]);
  const noLabel = useMemo(() => intl.formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' }), [intl]);

  const getLocalizedObjectName = useCallback(
    (obj: IObject | any): string => {
      const currentLocale = intl.locale.split('-')[0];
      if (obj.localization) {
        return obj.localization[currentLocale] || obj.localization.en || obj.name;
      }
      return obj.name;
    },
    [intl.locale],
  );

  const getSortedAttributes = useCallback((attrs: AttributeModel[]): AttributeModel[] => {
    if (!attrs.length) return [];
    const map = new Map(attrs.map((a) => [a.id, a]));
    const sorted: AttributeModel[] = [];
    let curr: number | null = null;
    for (const a of attrs) {
      if (a.predecessorId === null) {
        curr = a.id;
        break;
      }
    }
    while (curr !== null && map.has(curr)) {
      const a = map.get(curr)!;
      sorted.push(a);
      curr = attrs.find((n) => n.predecessorId === a.id)?.id || null;
    }
    attrs.forEach((a) => {
      if (!sorted.includes(a)) sorted.push(a);
    });
    return sorted;
  }, []);


  const createUpdatedModel = useCallback(
    (overrides: Partial<IElement>) => {
      if (!element) return null;
      const newModel = new ElementModel(
        'type' in overrides ? overrides.type! : element.type,
        'length' in overrides ? overrides.length! : element.length,
        'width' in overrides ? overrides.width! : element.width,
        'position' in overrides ? overrides.position! : element.position,
        'rotation' in overrides ? overrides.rotation! : element.rotation,
        'texture' in overrides ? overrides.texture : element.texture,
        'description' in overrides ? overrides.description : element.description,
        'useAsTextField' in overrides ? overrides.useAsTextField : element.useAsTextField,
        'fontSize' in overrides ? overrides.fontSize : element.fontSize,
        'object' in overrides ? overrides.object : element.object,
        'attributes' in overrides ? overrides.attributes : element.attributes,
        'sideC' in overrides ? overrides.sideC : element.sideC,
        'triangleAngle' in overrides ? overrides.triangleAngle : element.triangleAngle,
      );
      newModel.setUUID(element.uuid);
      return newModel;
    },
    [element],
  );

  const getProductTextureAttributes = useCallback((texture: ITexture): AttributeModel[] => {
    if (texture.textureOrigin !== ETextureOrigin.PRODUCT) return [];
    const prodTexture = texture as IProductTexture;
    const objectAttrs = prodTexture.objects?.flatMap((obj) => obj.attributes || []) || [];
    const objectAttrIds = new Set(objectAttrs.map((a) => a.id));
    const productAttrs = (prodTexture.attributes || []).filter((a) => !objectAttrIds.has(a.id));
    return [...productAttrs, ...objectAttrs];
  }, []);

  const handleAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      if (!element || !element.object) return;

      const updatedAttrs = element.object.attributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit,
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );

      const updatedObject = new ObjectModel(element.object.id, element.object.name, updatedAttrs, undefined, element.object.localization, element.object.ifcObject);
      const newModel = createUpdatedModel({ object: updatedObject });
      if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
    },
    [element, selectedElement.selectedElementIndex, updateElement, createUpdatedModel],
  );

  useEffect(() => {
    if (!open || !element) return;
    (async () => {
      try {
        if (element.object === undefined) {
          const fetchedObjects = await filesApiService.getObjects(regionCode);
          const filtered = fetchedObjects.filter((o) => !DEFAULT_OBJECT_IDS.includes(o.id));
          setObjects(filtered);
          setError(null);
        }
      } catch (e) {
        setError(`Error fetching objects: ${(e as Error).message}`);
      }
    })();
  }, [open, element?.object, regionCode]);


  // Effect A: Sync Dimensions (Runs often)
  useEffect(() => {
    if (!element) {
      onClose();
      return;
    }
    setLength(initialLength);
    setWidth(initialWidth);
    setSideC(initialSideC);
    setRotation(initialRotation);
    setTextureRotation(initialTextureRotation);
    setShifting(element.texture?.offset ?? 0);
    
    setLengthError('');
    setWidthError('');
    setSideCError('');
  }, [element, initialLength, initialWidth, initialSideC, initialRotation, initialTextureRotation, onClose]);


  useEffect(() => {
    if (!element) return;

    setSelectedObjectLocal(element.object || null);
    setInputValue(element.object ? getLocalizedObjectName(element.object) : '');
    
    setAttributeValues(
      element.object?.attributes.reduce(
        (acc, a) => {
          if (a.value) acc[a.id] = `${a.value}`;
          return acc;
        },
        {} as Record<number, string>,
      ) || {},
    );
    setShowInputFields(
      element.object?.attributes.reduce(
        (acc, a) => {
          acc[a.id] = !!a.value;
          return acc;
        },
        {} as Record<number, boolean>,
      ) || {},
    );
    if (!element.object) {
      setAllAttributes([]);
      setAttrSelected(null);
      setAttrInputValue('');
      setAttrError(null);
    }
  }, [element?.uuid, getLocalizedObjectName]); // Dependencies strictly limited to Object identity

  useEffect(() => {
    if (!open || !selectedObjectLocal) return;
    (async () => {
      try {
        const attributes = await filesApiService.getAttributes(regionCode);
        setAllAttributes(attributes);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, selectedObjectLocal, regionCode]);

  const computeDescription = useCallback(
    (obj: ObjectModel | IObject, sortedAttrs: AttributeModel[]): string => {
      const objName = getLocalizedObjectName(obj);
      if (!sortedAttrs.length) return objName;

      const first = sortedAttrs[0];

      const currentLocale = intl.locale.split('-')[0];
      const attrKey = first.localization?.[currentLocale] || first.localization?.en || first.key;

      let part = attrKey;
      if (first.value) {
        if (first.type === EAttributeType.BOOL) {
          part += `:${first.value === 'true' ? yesLabel : noLabel}`;
        } else {
          let val = first.value;
          let unitLabel = '';

          if (first.type === EAttributeType.NUMBER && first.unit) {
            const isImperial = unitType === EUnitType.IMPERIAL;
            const storedUnit = first.unit as EAttributeUnit;
            const targetUnit = getCorrespondingUnit(storedUnit, isImperial);

            if (targetUnit !== storedUnit) {
              const num = parseFloat(val);
              if (!isNaN(num)) {
                const converted = convertAttributeValue(num, storedUnit, isImperial);
                val = String(Math.round(converted * 1000) / 1000);
                unitLabel = attributeUnitDisplayMap[targetUnit] || targetUnit;
              } else {
                unitLabel = attributeUnitDisplayMap[storedUnit] || storedUnit;
              }
            } else {
              unitLabel = attributeUnitDisplayMap[storedUnit] || storedUnit;
            }
          }

          part += `:${val}`;
          if (unitLabel) {
            part += ` ${unitLabel}`;
          }
        }
      }

      return `${objName}\n${part}`;
    },
    [yesLabel, noLabel, getLocalizedObjectName, intl.locale, unitType],
  );

  const handleAttributeSelection = useCallback(
    async (selectedAttr: AttributeSelectionInput) => {
      setAttrSelected(typeof selectedAttr === 'string' ? null : selectedAttr);
      setAttrError(null);
      if (!selectedAttr || !element || !element.object) return;

      const existing = element.object.attributes || [];
      let resolvedAttribute: IAttribute | null = null;

      try {
        resolvedAttribute = await resolveAttributeSelection({
          attributeCatalog: allAttributes,
          currentAttributes: existing,
          locale: intl.locale,
          onAttributeCreated: (createdAttribute) => {
            setAllAttributes((prev) => mergeAttributesById(prev, [createdAttribute]));
            filesApiService
              .getAttributes(regionCode)
              .then((refreshedAttributes) => {
                setAllAttributes((prev) => mergeAttributesById(refreshedAttributes, prev, [createdAttribute]));
              })
              .catch(() => undefined);
          },
          selection: selectedAttr,
        });
      } catch {
        setAttrError(
          intl.formatMessage({
            id: 'AttributeAutocomplete.createAttributeError',
            defaultMessage: 'Attribute could not be created. Please make sure you are logged in.',
          }),
        );
        return;
      }

      if (!resolvedAttribute) return;
      if (existing.some((attribute) => attribute.id === resolvedAttribute.id)) {
        setAttrSelected(null);
        setAttrInputValue('');
        return;
      }

      const sorted = getSortedAttributes(existing);
      const pred = sorted.length ? sorted[sorted.length - 1].id : null;

      const newAttr = new AttributeModel(
        resolvedAttribute.id,
        resolvedAttribute.key,
        undefined,
        pred,
        resolvedAttribute.type as EAttributeType,
        resolvedAttribute.unit,
        resolvedAttribute.hidden,
        resolvedAttribute.forced,
        resolvedAttribute.ownerId,
        resolvedAttribute.userId,
        resolvedAttribute.publicFlag,
        resolvedAttribute.localization,
        resolvedAttribute.defaults,
        resolvedAttribute.validation,
        resolvedAttribute.unitFamily,
      );

      const updatedAttrs = [...existing, newAttr];
      const updatedObject = new ObjectModel(element.object.id, element.object.name, updatedAttrs, undefined, element.object.localization, element.object.ifcObject);
      const newDescription = computeDescription(updatedObject, getSortedAttributes(updatedAttrs));

      const newModel = createUpdatedModel({ object: updatedObject, description: newDescription });
      if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);

      setAttrSelected(null);
      setAttrInputValue('');
    },
    [allAttributes, element, getSortedAttributes, intl, selectedElement.selectedElementIndex, updateElement, computeDescription, createUpdatedModel, regionCode],
  );

  const handleDimensionChange = (valStr: string, setter: React.Dispatch<React.SetStateAction<number>>, errorSetter: React.Dispatch<React.SetStateAction<string>>, errorId: string) => {
    const v = parseFloat(valStr);
    if (isNaN(v)) return;
    const adj = unitType === EUnitType.METRIC ? v : Math.max(1, Math.round(v));
    setter(adj);
    const min = unitType === EUnitType.METRIC ? MIN_MM : Math.max(1, Math.round(MIN_MM / 25.4));
    const max = unitType === EUnitType.METRIC ? MAX_MM : Math.round(MAX_MM / 25.4);
    if (adj < min || adj > max) {
      errorSetter(
        intl.formatMessage(
          { id: errorId, defaultMessage: 'Value must be between {min} and {max} {unit}.' },
          { min, max, unit: unitType === EUnitType.METRIC ? mmLabel : inLabel },
        ),
      );
    } else {
      errorSetter('');
    }
  };

  const commitTriangleSide = useCallback(
    (changedSide: 'A' | 'B' | 'C') => {
      if (!element) return;

      const min = unitType === EUnitType.METRIC ? MIN_MM : Math.max(1, Math.round(MIN_MM / 25.4));
      const max = unitType === EUnitType.METRIC ? MAX_MM : Math.round(MAX_MM / 25.4);

      // Helper to clamp and convert to internal
      const process = (val: number, setter: React.Dispatch<React.SetStateAction<number>>, errorSetter: React.Dispatch<React.SetStateAction<string>>) => {
        let c = val;
        if (c < min) c = min;
        if (c > max) c = max;
        setter(c);
        errorSetter('');
        return unitType === EUnitType.METRIC ? c * MM_TO_INTERNAL_FACTOR : inchToInternalRounded(c);
      };

      // CORRECTION: Side A maps to LENGTH (Left/Vertical in user system), Side B maps to WIDTH (Below/Horizontal in user system)
      const internalA = changedSide === 'A' ? process(length, setLength, setLengthError) : element.length;
      const internalB = changedSide === 'B' ? process(width, setWidth, setWidthError) : element.width;
      let internalC = changedSide === 'C' ? process(sideC, setSideC, setSideCError) : element.sideC || 0;
      
      // Default to 90 degrees (in Radians) if undefined
      let internalAngleRad = element.triangleAngle || (Math.PI / 2);

      if (changedSide === 'A' || changedSide === 'B') {
        // Recalculate C based on Angle (Cosine Law)
        internalC = Math.sqrt(Math.pow(internalA, 2) + Math.pow(internalB, 2) - 2 * internalA * internalB * Math.cos(internalAngleRad));
        
        setSideC(unitType === EUnitType.METRIC ? Math.round(internalC / MM_TO_INTERNAL_FACTOR) : Math.round(internalToInchesRounded(internalC)));
      } else if (changedSide === 'C') {
        // Recalculate Angle based on C (Inverse Cosine Law)
        if (internalA + internalB <= internalC || internalA + internalC <= internalB || internalB + internalC <= internalA) {
          setSideCError(intl.formatMessage({ id: 'CreateElementsDrawer.error.impossibleTriangle', defaultMessage: 'Triangle impossible' }));
          return;
        } 
        
        const cosGamma = (Math.pow(internalA, 2) + Math.pow(internalB, 2) - Math.pow(internalC, 2)) / (2 * internalA * internalB);
        const clampedCos = Math.max(-1, Math.min(1, cosGamma));
        // Math.acos returns Radians - perfect for the model
        internalAngleRad = Math.acos(clampedCos);
      }

      // Map internalA to length, internalB to width
      const newModel = createUpdatedModel({ length: internalA, width: internalB, sideC: internalC, triangleAngle: internalAngleRad });
      if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
    },
    [element, length, width, sideC, unitType, mmLabel, inLabel, intl, selectedElement.selectedElementIndex, updateElement, createUpdatedModel],
  );

  const handleLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (isNaN(v)) return;
    const adj = unitType === EUnitType.METRIC ? v : Math.max(1, Math.round(v));
    setLength(adj);
    const min = unitType === EUnitType.METRIC ? MIN_MM : Math.max(1, Math.round(MIN_MM / 25.4));
    const max = unitType === EUnitType.METRIC ? MAX_MM : Math.round(MAX_MM / 25.4);
    if (adj < min || adj > max) {
      setLengthError(
        intl.formatMessage(
          { id: 'ElementDrawer.lengthError', defaultMessage: 'Value must be between {min} and {max} {unit}.' },
          { min, max, unit: unitType === EUnitType.METRIC ? mmLabel : inLabel },
        ),
      );
    } else {
      setLengthError('');
    }
  };
  const commitLength = useCallback(() => {
    if (!element) return;
    const min = unitType === EUnitType.METRIC ? MIN_MM : Math.max(1, Math.round(MIN_MM / 25.4));
    const max = unitType === EUnitType.METRIC ? MAX_MM : Math.round(MAX_MM / 25.4);
    let clamped = length;
    if (length < min) clamped = min;
    if (length > max) clamped = max;
    setLength(clamped);
    setLengthError('');
    const internal = unitType === EUnitType.METRIC ? clamped * MM_TO_INTERNAL_FACTOR : inchToInternalRounded(clamped);

    const newModel = createUpdatedModel({ length: internal });
    if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
  }, [length, unitType, selectedElement.selectedElementIndex, element, updateElement, createUpdatedModel]);
  const handleLengthBlur = () => commitLength();
  const handleLengthKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitLength();
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (isNaN(v)) return;
    const adj = unitType === EUnitType.METRIC ? v : Math.max(1, Math.round(v));
    setWidth(adj);
    const min = unitType === EUnitType.METRIC ? MIN_MM : Math.max(1, Math.round(MIN_MM / 25.4));
    const max = unitType === EUnitType.METRIC ? MAX_MM : Math.round(MAX_MM / 25.4);
    if (adj < min || adj > max) {
      setWidthError(
        intl.formatMessage(
          { id: 'ElementDrawer.widthError', defaultMessage: 'Value must be between {min} and {max} {unit}.' },
          { min, max, unit: unitType === EUnitType.METRIC ? mmLabel : inLabel },
        ),
      );
    } else {
      setWidthError('');
    }
  };
  const commitWidth = useCallback(() => {
    if (!element) return;
    const min = unitType === EUnitType.METRIC ? MIN_MM : Math.max(1, Math.round(MIN_MM / 25.4));
    const max = unitType === EUnitType.METRIC ? MAX_MM : Math.round(MAX_MM / 25.4);
    let clamped = width;
    if (width < min) clamped = min;
    if (width > max) clamped = max;
    setWidth(clamped);
    setWidthError('');
    const internal = unitType === EUnitType.METRIC ? clamped * MM_TO_INTERNAL_FACTOR : inchToInternalRounded(clamped);

    const newModel = createUpdatedModel({ width: internal });
    if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
  }, [width, unitType, selectedElement.selectedElementIndex, element, updateElement, createUpdatedModel]);
  const handleWidthBlur = () => commitWidth();
  const handleWidthKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitWidth();
  };

  const handleRotationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (!isNaN(v)) setRotation(Math.round(((v % 360) + 360) % 360));
  };
  const commitRotation = useCallback(() => {
    if (!element) return;
    const norm = Math.round(((rotation % 360) + 360) % 360);
    setRotation(norm);

    const newModel = createUpdatedModel({ rotation: (norm * Math.PI) / 180 });
    if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
  }, [rotation, selectedElement.selectedElementIndex, element, updateElement, createUpdatedModel]);
  const handleRotationBlur = () => commitRotation();
  const handleRotationKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitRotation();
  };

  const updateTexture = useCallback(
    (prop: keyof ITexture, v: number) => {
      const idx = selectedElement.selectedElementIndex;
      const el = allElements[idx];
      if (!el || !el.texture) return;

      const updated: ITexture = {
        ...el.texture,
        [prop]: prop === 'rotation' ? (v * Math.PI) / 180 : v,
      };

      const newModel = new ElementModel(
        el.type,
        el.length,
        el.width,
        el.position,
        el.rotation,
        updated,
        el.description,
        el.useAsTextField,
        el.fontSize,
        el.object,
        el.attributes,
        el.sideC,
        el.triangleAngle,
      );
      newModel.setUUID(el.uuid);

      updateElement(idx, newModel);
    },
    [allElements, selectedElement.selectedElementIndex, updateElement],
  );

  const handleObjectChange = useCallback(
    async (obj: IFilesApiObject | null) => {

      if (!element) return;
      setSelectedObjectLocal(obj);
      let model: ObjectModel | undefined;
      let newDescription: string | undefined;

      if (obj) {
        const details = await filesApiService.getObjectById(obj.id, regionCode);
        if (!details) {
          setError(`Error fetching object details: No data returned`);
          return;
        }
        const attrs = details.attributes || [];
        model = new ObjectModel(obj.id, obj.name, attrs, undefined, details.localization, details.ifcObject);
        newDescription = computeDescription(model, getSortedAttributes(attrs));
      }

      const newModel = createUpdatedModel({ object: model, description: newDescription });
      if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);

      setAttributeValues(
        model?.attributes.reduce(
          (acc, a) => {
            if (a.value) acc[a.id] = `${a.value}`;
            return acc;
          },
          {} as Record<number, string>,
        ) || {},
      );
      setShowInputFields(
        model?.attributes.reduce(
          (acc, a) => {
            acc[a.id] = !!a.value;
            return acc;
          },
          {} as Record<number, boolean>,
        ) || {},
      );
    },
    [computeDescription, getSortedAttributes, element, selectedElement.selectedElementIndex, updateElement, regionCode, createUpdatedModel],
  );

  const handleRemoveObject = () => {
    setIsConfirmationDialogOpen(true);
  };

  const handleAttributeValueChange = (attrId: number, val: string) => {
    if (!element || !element.object) return;

    setAttributeValues((prev) => ({ ...prev, [attrId]: val }));
    setShowInputFields((prev) => ({ ...prev, [attrId]: true }));

    const updatedAttrs = element.object.attributes.map((a) =>
      a.id === attrId
        ? new AttributeModel(
            a.id,
            a.key,
            val,
            a.predecessorId,
            a.type,
            a.unit,
            a.hidden,
            a.forced,
            a.ownerId,
            a.userId,
            a.publicFlag,
            a.localization,
            a.defaults,
            a.validation,
            a.unitFamily,
          )
        : a,
    );

    const sortedAttrs = getSortedAttributes(updatedAttrs);

    const updatedObject = new ObjectModel(element.object.id, element.object.name, updatedAttrs, undefined, element.object.localization, element.object.ifcObject);

    const isFirst = sortedAttrs.length > 0 && sortedAttrs[0].id === attrId;
    const newDesc = isFirst ? computeDescription(updatedObject, sortedAttrs) : element.description;

    const newModel = createUpdatedModel({ object: updatedObject, description: newDesc });
    if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
  };

  const handleDeleteAttribute = (attrId: number) => {
    if (!element || !element.object) return;
    const toDel = element.object.attributes.find((a) => a.id === attrId)!;

    let remaining = element.object.attributes
      .filter((a) => a.id !== attrId)
      .map((a) =>
        a.predecessorId === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              toDel.predecessorId,
              a.type,
              a.unit,
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );

    const sortedRemaining = getSortedAttributes(remaining);

    if (sortedRemaining.length > 0 && toDel.predecessorId === null) {
      remaining = remaining.map((a) =>
        a.id === sortedRemaining[0].id
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              null,
              a.type,
              a.unit,
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
    }

    const updatedObject = new ObjectModel(element.object.id, element.object.name, remaining, undefined, element.object.localization, element.object.ifcObject);

    const newDescription = computeDescription(updatedObject, getSortedAttributes(remaining));

    const newModel = createUpdatedModel({ object: updatedObject, description: newDescription });
    if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);

    setAttributeValues((prev) => {
      const c = { ...prev };
      delete c[attrId];
      return c;
    });
    setShowInputFields((prev) => {
      const c = { ...prev };
      delete c[attrId];
      return c;
    });
  };

  const toggleInputField = (attrId: number) => {
    setShowInputFields((prev) => ({ ...prev, [attrId]: !prev[attrId] }));
  };

  const sortedAttrs = element && element.object ? getSortedAttributes(element.object.attributes) : [];

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const handleCancelObjectDelete = () => setIsConfirmationDialogOpen(false);
  const handleConfirmObjectDelete = () => {
    if (!element) return;
    setSelectedObjectLocal(null);
    setInputValue('');
    setAttributeValues({});
    setShowInputFields({});
    setAllAttributes([]);
    setAttrSelected(null);
    setAttrInputValue('');
    setAttrError(null);

    const newModel = createUpdatedModel({ object: undefined, description: '' });
    if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
    setIsConfirmationDialogOpen(false);
  };

  const handleDeleteElement = () => {
    deleteElement(element.uuid);
    resetSelection();
    onClose();
  };

  if (!element) return null;

  const wheelConfig: WheelInputConfig = {
    unitType,
    minMetric: MIN_MM,
    maxMetric: MAX_MM,
    minInches: Math.max(1, Math.round(MIN_MM / 25.4)),
    maxInches: Math.round(MAX_MM / 25.4),
  };
  const rotationWheelConfig: WheelInputConfig = {
    unitType,
    minMetric: 0,
    maxMetric: 360,
    minInches: 0,
    maxInches: 360,
  };
  const textureExists = Boolean(
    element.texture && ((typeof element.texture.color === 'string' && element.texture.color.trim() !== '') || (element.texture.id !== null && element.texture.id !== -1)),
  );
  const iconWrapperSx = { display: 'flex', alignItems: 'center', '& svg': { fontSize: 20, width: '0.9em', height: '0.9em', fill: 'none', stroke: '#9B9B9B' } };

  return (
    <>
      <Drawer
        key={id}
        anchor="right"
        open={open}
        variant={open ? 'permanent' : 'temporary'}
        sx={{
          '& .MuiPaper-root': {
            top: '64px',
            zIndex: 1000,
            width: '420px',
            height: 'calc(100% - 64px)',
          },
        }}
      >
        <Grid container direction="column" spacing={0} p={0}>
          <Grid
            item
            sx={{
              backgroundColor: immersightBlue,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 0.5,
            }}
          >
            <Box sx={{ ml: 2 }}>
              <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
                {intl.formatMessage({
                  id: 'ElementDrawer.header',
                  defaultMessage: 'Element definieren',
                })}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#fff', '&:hover': { background: 'transparent' } }}>
              <PlayArrow sx={{ fontSize: 30 }} />
            </IconButton>
          </Grid>

          <Grid container direction="column" spacing={2} p={2}>
            {!element.useAsTextField && (
              <>
                {element.type === EElementType.TRIANGLE ? (
                  <>
                    <Grid item>
                      <TextField
                        size="small"
                        fullWidth
                        label={`${intl.formatMessage({ id: 'CreateElementsDrawer.triangle.sideA', defaultMessage: 'Seite A' })} (${unitType === EUnitType.METRIC ? mmLabel : inLabel})`}
                        type="number"
                        value={length}
                        onChange={(e) => handleDimensionChange(e.target.value, setLength, setLengthError, 'ElementDrawer.lengthError')}
                        onBlur={() => commitTriangleSide('A')}
                        onKeyUp={(e) => e.key === 'Enter' && commitTriangleSide('A')}
                        onWheel={(e) => {
                          e.stopPropagation();
                          handleWheelInput(
                            e,
                            length.toString(),
                            (v) => {
                              const nv = parseFloat(v);
                              if (!isNaN(nv)) setLength(nv);
                            },
                            wheelConfig,
                          );
                        }}
                        error={!!lengthError}
                        helperText={lengthError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box component="span" sx={iconWrapperSx}>
                                <IconTriangleSideA />
                              </Box>
                            </InputAdornment>
                          ),
                          inputProps: { min: wheelConfig.minMetric, max: wheelConfig.maxMetric, step: 1 },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        size="small"
                        fullWidth
                        label={`${intl.formatMessage({ id: 'CreateElementsDrawer.triangle.sideB', defaultMessage: 'Seite B' })} (${unitType === EUnitType.METRIC ? mmLabel : inLabel})`}
                        type="number"
                        value={width}
                        onChange={(e) => handleDimensionChange(e.target.value, setWidth, setWidthError, 'ElementDrawer.widthError')}
                        onBlur={() => commitTriangleSide('B')}
                        onKeyUp={(e) => e.key === 'Enter' && commitTriangleSide('B')}
                        onWheel={(e) => {
                          e.stopPropagation();
                          handleWheelInput(
                            e,
                            width.toString(),
                            (v) => {
                              const nv = parseFloat(v);
                              if (!isNaN(nv)) setWidth(nv);
                            },
                            wheelConfig,
                          );
                        }}
                        error={!!widthError}
                        helperText={widthError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box component="span" sx={iconWrapperSx}>
                                <IconTriangleSideB />
                              </Box>
                            </InputAdornment>
                          ),
                          inputProps: { min: wheelConfig.minMetric, max: wheelConfig.maxMetric, step: 1 },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        size="small"
                        fullWidth
                        label={`${intl.formatMessage({ id: 'CreateElementsDrawer.triangle.sideC', defaultMessage: 'Seite C' })} (${unitType === EUnitType.METRIC ? mmLabel : inLabel})`}
                        type="number"
                        value={sideC}
                        onChange={(e) => handleDimensionChange(e.target.value, setSideC, setSideCError, 'ElementDrawer.lengthError')}
                        onBlur={() => commitTriangleSide('C')}
                        onKeyUp={(e) => e.key === 'Enter' && commitTriangleSide('C')}
                        onWheel={(e) => {
                          e.stopPropagation();
                          handleWheelInput(
                            e,
                            sideC.toString(),
                            (v) => {
                              const nv = parseFloat(v);
                              if (!isNaN(nv)) setSideC(nv);
                            },
                            wheelConfig,
                          );
                        }}
                        error={!!sideCError}
                        helperText={sideCError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box component="span" sx={iconWrapperSx}>
                                <IconTriangleSideC />
                              </Box>
                            </InputAdornment>
                          ),
                          inputProps: { min: wheelConfig.minMetric, max: wheelConfig.maxMetric, step: 1 },
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item>
                      <TextField
                        size="small"
                        fullWidth
                        label={`${intl.formatMessage({
                          id: 'ElementDrawer.length',
                          defaultMessage: 'Länge',
                        })} (${unitType === EUnitType.METRIC ? mmLabel : inLabel})`}
                        type="number"
                        value={length}
                        onChange={handleLengthChange}
                        onBlur={handleLengthBlur}
                        onKeyUp={handleLengthKeyPress}
                        onWheel={(e) => {
                          e.stopPropagation();
                          handleWheelInput(
                            e,
                            length.toString(),
                            (v) => {
                              const nv = parseFloat(v);
                              if (!isNaN(nv)) setLength(nv);
                            },
                            wheelConfig,
                          );
                        }}
                        error={!!lengthError}
                        helperText={lengthError}
                        InputProps={{ inputProps: { min: wheelConfig.minMetric, max: wheelConfig.maxMetric, step: 1 } }}
                      />
                    </Grid>

                    <Grid item>
                      <TextField
                        size="small"
                        fullWidth
                        label={`${intl.formatMessage({
                          id: 'ElementDrawer.width',
                          defaultMessage: 'Breite',
                        })} (${unitType === EUnitType.METRIC ? mmLabel : inLabel})`}
                        type="number"
                        value={width}
                        onChange={handleWidthChange}
                        onBlur={handleWidthBlur}
                        onKeyUp={handleWidthKeyPress}
                        onWheel={(e) => {
                          e.stopPropagation();
                          handleWheelInput(
                            e,
                            width.toString(),
                            (v) => {
                              const nv = parseFloat(v);
                              if (!isNaN(nv)) setWidth(nv);
                            },
                            wheelConfig,
                          );
                        }}
                        error={!!widthError}
                        helperText={widthError}
                        InputProps={{ inputProps: { min: wheelConfig.minMetric, max: wheelConfig.maxMetric, step: 1 } }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item>
                  <TextField
                    size="small"
                    fullWidth
                    label={intl.formatMessage({
                      id: 'ElementDrawer.rotation',
                      defaultMessage: 'Rotation °',
                    })}
                    type="number"
                    value={rotation}
                    onChange={handleRotationChange}
                    onBlur={handleRotationBlur}
                    onKeyUp={handleRotationKeyPress}
                    onWheel={(e) => {
                      e.stopPropagation();
                      handleWheelInput(
                        e,
                        rotation.toString(),
                        (v) => {
                          const nv = parseFloat(v);
                          if (!isNaN(nv)) setRotation(nv);
                        },
                        rotationWheelConfig,
                      );
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <RotateRight />
                        </InputAdornment>
                      ),
                      inputProps: { min: 0, max: 360, step: 1 },
                    }}
                  />
                </Grid>

                <Grid item>
                  {error ? (
                    <Typography color="error">{error}</Typography>
                  ) : element.object ? (
                    <>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography variant="body1">
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {intl.formatMessage({
                              id: 'ElementDrawer.object',
                              defaultMessage: 'Object type',
                            })}
                          </Box>
                          {`: ${getLocalizedObjectName(element.object)}`}
                        </Typography>
                        <Box display="flex" gap={0.5}>
                          <Box width="32px" />
                          <Tooltip
                            title={intl.formatMessage({
                              id: 'ElementDrawer.resetObject',
                              defaultMessage: 'Objekt zurücksetzen',
                            })}
                          >
                            <IconButton sx={{ color: '#464646' }} onClick={handleRemoveObject}>
                              <IconReset />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <AttributeList
                        attributes={sortedAttrs}
                        attributeValues={attributeValues}
                        showInputFields={showInputFields}
                        onAttributeValueChange={handleAttributeValueChange}
                        onAttributeUnitChange={handleAttributeUnitChange}
                        onDeleteAttribute={handleDeleteAttribute}
                        onToggleInputField={toggleInputField}
                        onReorderAttributes={(newAttributes) => {
                          if (!element.object) return;
                          const newObject = new ObjectModel(
                            element.object.id,
                            element.object.name,
                            newAttributes,
                            undefined,
                            element.object.localization,
                            element.object.ifcObject,
                          );
                          const newDesc = computeDescription(newObject, getSortedAttributes(newAttributes));
                          const newModel = createUpdatedModel({ object: newObject, description: newDesc });
                          if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
                        }}
                      />

                      <Grid item>
                        <Box
                          sx={{
                            mt: -3,
                            mb: 1,
                            width: '99%',
                            mx: 'auto',
                          }}
                        >
                          <AttributeAutocomplete
                            attributes={allAttributes}
                            disabledAttributeIds={element.object!.attributes.map((attribute) => attribute.id)}
                            selected={attrSelected}
                            inputValue={attrInputValue}
                            error={attrError}
                            onChange={handleAttributeSelection}
                            onInputChange={(val) => {
                              setAttrInputValue(val);
                              if (val !== attrSelected?.key) setAttrSelected(null);
                            }}
                            placeholder={intl.formatMessage({
                              id: 'AttributeAutocomplete.addAttribute',
                              defaultMessage: 'Attribut hinzufügen',
                            })}
                            width="100%"
                          />
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: immersightBlue,
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                          mb: 1,
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ElementDrawer.assignObject',
                          defaultMessage: 'ASSIGN OBJECT',
                        })}
                      </Typography>
                      <ObjectAutocomplete
                        objects={objects}
                        selected={selectedObjectLocal}
                        inputValue={inputValue}
                        onChange={handleObjectChange}
                        onInputChange={setInputValue}
                        placeholder={intl.formatMessage({
                          id: 'ElementDrawer.selectObjectType',
                          defaultMessage: 'Objekttyp wählen',
                        })}
                      />
                    </>
                  )}
                </Grid>

                <Grid item>
                  <Typography variant="subtitle2" sx={{ color: immersightBlue, textTransform: 'uppercase' }}>
                    {intl.formatMessage({
                      id: 'ElementDrawer.surface',
                      defaultMessage: 'OBERFLÄCHE',
                    })}
                  </Typography>
                </Grid>
                {!textureExists && (
                  <Grid item>
                    <Button
                      variant="contained"
                      startIcon={<Texture />}
                      onClick={() => {
                        setSelectedObject(element);
                        setActiveDrawer(EActiveDrawer.TEXTURES);
                      }}
                      sx={{ backgroundColor: immersightBlue, color: '#fff', textTransform: 'uppercase' }}
                    >
                      {intl.formatMessage({
                        id: 'ElementDrawer.changeSurface',
                        defaultMessage: 'OBERFLÄCHE ÄNDERN',
                      })}
                    </Button>
                  </Grid>
                )}
                {textureExists && element.texture && (
                  <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: element.texture.color || 'transparent',
                        backgroundImage: element.texture.material ? `url(${element.texture.material.image.src})` : 'none',
                        backgroundSize: 'cover',
                        border: element.texture.color ? '1px solid #ccc' : 'none',
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        maxWidth: 120,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {element.texture.name.length > 20 ? `${element.texture.name.slice(0, 20)}...` : element.texture.name}
                    </Typography>
                    <IconButton
                      sx={{ color: '#464646' }}
                      onClick={() => {
                        const newModel = createUpdatedModel({ texture: null });
                        if (newModel) updateElement(selectedElement.selectedElementIndex, newModel);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                )}
                {/* --- CATEGORIES SECTION --- */}
                {element.texture && 'objects' in element.texture && element.texture.objects && element.texture.objects.length > 0 && (
                  <Grid item>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      style={{
                        color: '#4a4a4a',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {intl.formatMessage({ id: 'RoomComponentsSearchPanel.categories', defaultMessage: 'Categories' })}
                      {': '}
                      {element.texture.objects.map((obj) => getLocalizedObjectName(obj)).join(', ')}
                    </Typography>
                  </Grid>
                )}
                {element.texture?.material && (
                  <>
                    <Grid item>
                      <Typography variant="subtitle2">
                        {intl.formatMessage({
                          id: 'ElementDrawer.textureShifting',
                          defaultMessage: 'Verschieben',
                        })}
                      </Typography>
                      <Slider
                        value={shifting}
                        min={-2}
                        max={2}
                        step={0.1}
                        onChange={(_, v) => {
                          const nv = v as number;
                          setShifting(nv);
                          updateTexture('offset', nv);
                        }}
                        valueLabelDisplay="auto"
                        sx={{
                          color: immersightBlue,
                          '& .MuiSlider-thumb': { backgroundColor: immersightGreyLighter3 },
                          '& .MuiSlider-rail': { backgroundColor: immersightBlue75 },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle2">
                        {intl.formatMessage({
                          id: 'ElementDrawer.textureRotation',
                          defaultMessage: 'Rotieren',
                        })}
                      </Typography>
                      <Slider
                        value={textureRotation}
                        min={0}
                        max={360}
                        step={1}
                        onChange={(_, v) => {
                          const nv = v as number;
                          setTextureRotation(nv);
                          updateTexture('rotation', nv);
                        }}
                        valueLabelDisplay="auto"
                        sx={{
                          color: immersightBlue,
                          '& .MuiSlider-thumb': { backgroundColor: immersightGreyLighter3 },
                          '& .MuiSlider-rail': { backgroundColor: immersightBlue75 },
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* --- TEXTURE ATTRIBUTES SECTION --- */}
                {element.texture &&
                  element.texture.textureOrigin === ETextureOrigin.PRODUCT &&
                  (() => {
                    const prodTexture = element.texture as IProductTexture;
                    const textureAttrs = getProductTextureAttributes(element.texture);

                    if (textureAttrs.length > 0) {
                      return (
                        <Grid item>
                          <AttributeListFixed attributes={textureAttrs} originRegion={prodTexture.country} />
                        </Grid>
                      );
                    }
                    return null;
                  })()}
              </>
            )}

            <Grid item sx={{ mt: 4 }}>
              <Button variant="contained" startIcon={<DeleteIcon />} onClick={handleDeleteElement} sx={{ backgroundColor: immersightBlue, textTransform: 'uppercase' }}>
                {intl.formatMessage({
                  id: 'ElementDrawer.deleteElement',
                  defaultMessage: 'Element löschen',
                })}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Drawer>

      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        onClose={handleCancelObjectDelete}
        onConfirm={handleConfirmObjectDelete}
        titleId="ElementDrawer.confirmDeleteTitle"
        messageId="ElementDrawer.confirmDeleteMessage"
      />
    </>
  );
};

export default ElementDrawer;
